import {
  Button,
  closeCurrentDialog,
  Form,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Box, Center, Grid, HStack, Show, Space, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useId} from 'react';
import {Trans} from 'react-i18next';

import {isNil, isNotNil} from 'ramda';

import {
  EmailCredentialsOwnerType,
  SmtpEmailProviderCode,
  useCreateEmailSmtpCredentialsMutation,
  usePatchEmailSmtpCredentialsMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {TestIdProps} from 'shared/web';

import type {EmailServiceFormSchemaType} from '../types/EmailServiceFormSchemaType';
import {getSmtpProviderDocumentationLink} from '../utils/getSmtpProviderDocumentationLink';
import {getSmtpProviderLogo} from '../utils/getSmtpProviderLogo';
import {EmailServiceFormActions} from './EmailServiceFormActions';

const DEFAULT_TEST_ID = 'emailServiceForm';

interface EmailServiceFormProps extends TestIdProps {
  onChangeProvider?: () => void;
  providerCode?: SmtpEmailProviderCode;
  defaultValues: Partial<EmailServiceFormSchemaType>;
  id?: string;
  ownerType: EmailCredentialsOwnerType;
}

export function EmailServiceForm(props: EmailServiceFormProps) {
  const isCreating = isNil(props.id);
  const providerCode = props.providerCode ?? 'custom-smtp';

  const createCacheKey = useId();
  const patchCacheKey = useId();
  const [createSmtpCredentials, {reset: resetCreate}] = useCreateEmailSmtpCredentialsMutation({
    fixedCacheKey: createCacheKey,
  });
  const [patchSmtpCredentials, {reset: resetPatch}] = usePatchEmailSmtpCredentialsMutation({
    fixedCacheKey: patchCacheKey,
  });

  const handleSubmit: FormSubmitHandler<EmailServiceFormSchemaType> = async (values) => {
    const credentialsId = props.id;

    if (isNil(credentialsId)) {
      await createSmtpCredentials({
        createSmtpCredentialsRequestBody: {
          ownerType: props.ownerType,
          ...values,
        },
      })
        .unwrap()
        .then(() => {
          showNotification.success(i18n.t('general.notifications.successfullyCreated'));
          closeCurrentDialog();
        })
        .catch(handleApiError)
        .finally(resetCreate);
    } else {
      await patchSmtpCredentials({
        id: credentialsId,
        // Intentional allow-list: a new form field must not reach the PATCH API unreviewed.
        patchSmtpCredentialsRequestBody: {
          title: values.title,
          host: values.host,
          port: values.port,
          username: values.username,
          emailAddress: values.emailAddress,
          password: values.password,
        },
      })
        .unwrap()
        .then(() => {
          closeCurrentDialog();
        })
        .catch(handleApiError)
        .finally(resetPatch);
    }
  };

  const providerLogo = getSmtpProviderLogo(providerCode);
  const providerDocumentationLink = getSmtpProviderDocumentationLink(providerCode);

  return (
    <Form<EmailServiceFormSchemaType>
      defaultValues={props.defaultValues}
      onSubmit={handleSubmit}
      experimentalZodSchema={EmailServiceFormSchema}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <Show when={isNotNil(providerLogo)}>
            <Center width="100%" height="100%">
              <Space vertical={4} />
              <Box width={40} height={40}>
                <Center width="100%" height="100%">
                  {providerLogo}
                </Center>
              </Box>
            </Center>
          </Show>

          <VStack>
            <Text align="center" size="small" color="secondary">
              <Trans
                i18nKey={`entity.emailService.labels.${providerCode}-documentationMessage`}
                components={{
                  break: <br />,
                  bold: <Text alternative inline size="small" color="secondary" />,
                }}
              />
            </Text>

            <Button
              variant="link"
              title={i18n.t('general.actions.viewDocumentation')}
              onClick={() => window.open(providerDocumentationLink ?? '', '_blank')}
              leftIcon="action/open_in_new"
            />
          </VStack>

          <VStack spacing={4}>
            <FormField
              name="title"
              type="text"
              label={i18n.t('entity.emailService.labels.title')}
              control={control}
              tooltip={i18n.t(`entity.emailService.labels.${providerCode}-titleTooltip`)}
              isRequired
            />

            <FormField
              name="emailAddress"
              type="email"
              label={i18n.t('entity.emailService.labels.emailAddress')}
              tooltip={i18n.t(`entity.emailService.labels.${providerCode}-emailAddressTooltip`)}
              control={control}
              isRequired
            />

            <HStack spacing={4}>
              <Box flex={1}>
                <FormField
                  name="host"
                  type="text"
                  label={i18n.t('entity.emailService.labels.host')}
                  tooltip={i18n.t(`entity.emailService.labels.${providerCode}-hostTooltip`)}
                  control={control}
                  isRequired
                />
              </Box>
              <FormField
                name="port"
                type="number"
                label={i18n.t('entity.emailService.labels.port')}
                tooltip={i18n.t(`entity.emailService.labels.${providerCode}-portTooltip`)}
                control={control}
                isRequired
              />
            </HStack>

            <Grid columns={2}>
              <FormField
                name="username"
                type="text"
                label={i18n.t('entity.emailService.labels.userName')}
                tooltip={i18n.t(`entity.emailService.labels.${providerCode}-userNameTooltip`)}
                control={control}
                isRequired
              />

              <FormField
                name="password"
                type="password"
                label={i18n.t('entity.emailService.labels.password')}
                tooltip={i18n.t(`entity.emailService.labels.${providerCode}-passwordTooltip`)}
                helperText={
                  isCreating
                    ? undefined
                    : i18n.t('entity.emailService.labels.passwordRequiredForEditHint')
                }
                control={control}
                isRequired
              />
            </Grid>

            <EmailServiceFormActions
              control={control}
              formApi={formApi}
              isCreating={isCreating}
              onChangeProvider={props.onChangeProvider}
              data-testid={props['data-testid'] ?? DEFAULT_TEST_ID}
            />
          </VStack>
        </VStack>
      )}
    </Form>
  );
}

const EmailServiceFormSchema = z.object({
  title: z.string().min(1),
  host: z.string().min(1),
  port: z.number().min(1).max(65535),
  username: z.string().min(1),
  emailAddress: z.string().email(),
  password: z.string().min(1),
});
